//
// Détection PURE de l'aptitude au push (spec 13 §9) — sans dépendance aux API
// navigateur (injectées), donc entièrement testable. Sépare le SUPPORT (capacités
// + plateforme, stable) de la PERMISSION (lue à chaud, volatile).
//
import type { PushPlatform } from '@redpandev/shared';
import type { PwaPlatform } from '../pwa-install';

/**
 * - `unsupported` : le navigateur n'a pas Push/Notification/SW → encart explicatif.
 * - `ios-not-installed` : iOS mais pas en mode app → le push iOS EXIGE la PWA
 *   installée (16.4+, `display: standalone`) → encart « installe d'abord ».
 * - `supported` : le toggle peut demander la permission.
 */
export type PushSupport = 'unsupported' | 'ios-not-installed' | 'supported';

export interface PushCapabilities {
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  platform: PwaPlatform;
  isInstalled: boolean;
}

/** Capacités réelles de l'environnement courant (navigateur). */
export function readPushCapabilities(): Pick<
  PushCapabilities,
  'hasServiceWorker' | 'hasPushManager' | 'hasNotification'
> {
  return {
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    hasNotification: typeof window !== 'undefined' && 'Notification' in window,
  };
}

/** Décision de support (spec 13 §9), pure et testable. */
export function pushSupport(caps: PushCapabilities): PushSupport {
  if (!caps.hasServiceWorker || !caps.hasPushManager || !caps.hasNotification) {
    return 'unsupported';
  }
  if (caps.platform === 'ios' && !caps.isInstalled) return 'ios-not-installed';
  return 'supported';
}

/**
 * Plateforme STOCKÉE avec l'appareil (enum Prisma `PushPlatform`). v1 : tout
 * navigateur = `web` (web push), iOS PWA installée = `ios`. `android` natif est
 * un transport futur (Capacitor) — un Chrome Android reste `web`.
 */
export function toPushPlatform(platform: PwaPlatform): PushPlatform {
  return platform === 'ios' ? 'ios' : 'web';
}
